import { useState } from 'react';
import type { MouseEvent } from 'react';
import {
  isChecked,
  template,
  formatPhoneNumber,
  EMAIL,
  DEFAULT_PHONE,
} from '@belong/common';
import { IconClose, IconDownload, IconSpinner } from '@belong/icons';
import {
  Text,
  Button,
  ButtonBase,
  Form,
  CheckboxField,
  Image,
  Modal,
  Toast,
} from '@belong/ui';
import type {
  SignModel,
  EmployeeAssignmentDetailModel,
  AgreementDetailModel,
} from 'api/models';
import { clsx } from 'clsx';
import SidebarWithTextBubble from 'components/SidebarWithTextBubble/SidebarWithTextBubble';
import type { FormikHelpers } from 'formik';
import { AgreementSummary } from 'pages/AgreementFlow/AgreementFlowSteps/join/components/agreement-summary/agreement-summary';
import { BelongStandardsLink } from 'pages/AgreementFlow/AgreementFlowSteps/join/components/belong-standards-link/belong-standards-link';
import { fetchAgreementPdfUrl } from 'store/redux/agreement-flow/actions';
import { useAppDispatch } from 'store/redux/use-app-dispatch';
import { AGREEMENT_FLOW_STRINGS } from 'strings/agreement-flow.strings';
import AgreementRenderer from '../../AgreementRenderer/AgreementRenderer';
import css from './join.module.css';

type Props = {
  onSubmit: () => Promise<void>;
  currentStepFormData: SignModel;
  setLoading: (loading: boolean) => void;
  setSidebarTip: (tip: unknown) => void;
  openTipModal: () => void;
  employeeAssignment?: EmployeeAssignmentDetailModel;
  currentAgreement?: AgreementDetailModel;
  loading?: boolean;
};

export function Join({
  currentStepFormData,
  onSubmit,
  setLoading,
  openTipModal,
  setSidebarTip,
  employeeAssignment,
  currentAgreement,
  loading,
}: Props) {
  const [isAgreementModalVisible, setIsAgreementModalVisible] = useState(false);
  const [agreementTip, setAgreementTip] =
    useState<Record<'descriptions', string[]>>();
  const [isAgreementTipOverlayVisible, setIsAgreementTipOverlayVisible] =
    useState(false);
  const [isDownloadingAgreementPDF, setIsDownloadingAgreementPDF] =
    useState(false);
  const [isErrorToastVisible, setIsErrorToastVisible] = useState(false);
  // `useAppDispatch` rather than `useDispatch`: `clientMiddleware` resolves a
  // `promise` action to the client's result, which is what `response.url` reads.
  const dispatch = useAppDispatch();

  const hasAtLeastOneAdoptedHome = currentAgreement?.homeAgreements?.some(
    (homeAgreement) => homeAgreement.acquiredAsAdoptedHome,
  );

  async function handleDownloadClick(event: MouseEvent<HTMLButtonElement>) {
    try {
      setIsErrorToastVisible(false);

      const isMobileDownload = event.currentTarget.dataset.mobile === 'true';

      if (isMobileDownload) {
        /**
         * Since there is a layer conflict with the spinner shown via setLoading, we need to show create another
         * spinner that can be controlled independently from the other.
         * This can be for sure avoided but it would require making adjustements to core components like
         * FlowLayoutV2 and it is not worth the effort on this legacy stack.
         */
        setIsDownloadingAgreementPDF(true);
      } else {
        setLoading(true);
      }

      const response = await dispatch(
        fetchAgreementPdfUrl(currentStepFormData?.documentInstance?.uniqueId),
      );

      if (response.url) {
        window.open(response.url, '_blank');
      }
    } catch {
      setIsErrorToastVisible(true);
    } finally {
      setLoading(false);
      setIsDownloadingAgreementPDF(false);
    }
  }

  function getInitialValues(): Record<'signed', boolean> {
    return {
      signed: currentStepFormData?.documentSignature?.status === 'Signed',
    };
  }

  async function handleSubmit(
    _: Record<'signed', boolean>,
    // `FormikHelpers`, not `FormikBag`: the extra `props` member `FormikBag`
    // carries is not part of what `Form` calls `onSubmit` with.
    { setSubmitting }: FormikHelpers<Record<'signed', boolean>>,
  ) {
    try {
      setIsErrorToastVisible(false);

      await onSubmit();
    } catch {
      setIsErrorToastVisible(true);
    } finally {
      setSubmitting(false);
    }
  }

  function handleViewFullDocumentClick() {
    setIsAgreementModalVisible(true);
  }

  function handleAgreementModalDismiss() {
    setIsAgreementModalVisible(false);
  }

  function handleAgreementTipModalOpen() {
    setIsAgreementTipOverlayVisible(true);
  }

  function handleAgreementTipModalDismiss() {
    setIsAgreementTipOverlayVisible(false);
  }

  function handleErrorToastClose() {
    setIsErrorToastVisible(false);
  }

  function handleAddendumDownload(event: MouseEvent<HTMLButtonElement>) {
    const addendumURL = event.currentTarget.dataset.addendum;

    window.open(addendumURL, '_blank');
  }

  if (loading) {
    return null;
  }

  return (
    <>
      <div className="mb-3xl flex flex-col">
        <Text as="h1" variant="h2" fontWeight="semibold">
          {AGREEMENT_FLOW_STRINGS['sign.title']}
        </Text>
        <div className="my-xl sm:mb-2xl">
          <AgreementSummary isAdoptedInvitation={hasAtLeastOneAdoptedHome} />
        </div>
        <BelongStandardsLink />
        <div className="hidden sm:mb-sm sm:flex sm:items-center sm:justify-end">
          <Button onClick={handleDownloadClick} variant="text">
            {AGREEMENT_FLOW_STRINGS['sign.download']}
          </Button>
        </div>
        <div
          className={clsx(
            'relative h-4xl overflow-scroll rounded border-2 border-solid border-gray p-2sm sm:h-5xl sm:p-lg',
            (currentStepFormData?.addendums?.length ?? 0) > 0
              ? 'mb-sm'
              : 'mb-0 sm:mb-xl',
          )}
        >
          <ButtonBase
            className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-2xs bg-white/80 xl:hidden"
            onClick={handleViewFullDocumentClick}
          >
            <Image
              alt={AGREEMENT_FLOW_STRINGS['sign.download']}
              className="mb-xs h-lg w-lg"
              src="/agreement-flow/open_document.svg"
            />
            <Text fontWeight="semibold">
              {AGREEMENT_FLOW_STRINGS['sign.download.fullDocument']}
            </Text>
          </ButtonBase>
          {!isAgreementModalVisible && (
            <AgreementRenderer
              agreement={currentStepFormData?.documentInstance?.html}
              setTip={setSidebarTip}
              openTipModal={openTipModal}
            />
          )}
        </div>
        {(currentStepFormData?.addendums?.length ?? 0) > 0 && (
          <div className="mb-xl flex flex-col items-end gap-xs">
            {currentStepFormData?.addendums?.map((addendum) => {
              return (
                <Button
                  onClick={handleAddendumDownload}
                  variant="text"
                  key={addendum.id}
                  data-addendum={addendum.url}
                >
                  <Text
                    className="text-right"
                    variant="p2"
                    fontWeight="semibold"
                  >
                    {addendum.documentType}
                  </Text>
                </Button>
              );
            })}
          </div>
        )}
        <Form<Record<'signed', boolean>>
          initialValues={getInitialValues()}
          onSubmit={handleSubmit}
        >
          {({ values: { signed }, errors }) => (
            <>
              <div className="fixed bottom-0 left-0 flex w-full flex-col gap-lg bg-navy px-2sm py-lg sm:hidden">
                <CheckboxField
                  fontWeight={signed ? 'semibold' : 'regular'}
                  labelTextClassName={clsx(
                    signed ? 'text-green' : 'text-white',
                  )}
                  unselectedClassName={clsx(!errors.signed && 'text-white')}
                  size="small"
                  name="signed"
                  label={AGREEMENT_FLOW_STRINGS['sign.checkbox']}
                  variant="tertiary"
                  inputContainerClass="self-start"
                  validate={isChecked}
                />
                <Form.SubmitButton size="fluid">
                  {AGREEMENT_FLOW_STRINGS['sign.CTA']}
                </Form.SubmitButton>
              </div>
              <div className="hidden flex-col items-center justify-center gap-2xl sm:flex">
                <CheckboxField
                  fontWeight={signed ? 'semibold' : 'regular'}
                  labelClassName={css.signCheckbox}
                  labelTextClassName="text-navy"
                  unselectedClassName="text-navy"
                  size="small"
                  name="signed"
                  label={AGREEMENT_FLOW_STRINGS['sign.checkbox']}
                  inputContainerClass="self-start"
                  validate={isChecked}
                />
                <div className={css.signButton}>
                  <Form.SubmitButton className="h-full" size="fluid">
                    <Text fontWeight="semibold" className={css.signButtonText}>
                      {AGREEMENT_FLOW_STRINGS['sign.CTA']}
                    </Text>
                  </Form.SubmitButton>
                </div>
              </div>
            </>
          )}
        </Form>
      </div>
      <div className="contents xl:hidden">
        <Modal
          isOpen={isAgreementModalVisible}
          name="agreement-modal"
          onDismiss={handleAgreementModalDismiss}
          size="fullScreen"
          closeButton={({ onDismiss }) => {
            return (
              <div className="fixed top-0 z-20 flex w-full justify-end gap-xs bg-white p-xs">
                <ButtonBase
                  className="flex h-xl w-xl items-center justify-center"
                  onClick={handleDownloadClick}
                  data-mobile="true"
                >
                  <IconDownload width={20} />
                </ButtonBase>
                <ButtonBase
                  className="flex h-xl w-xl items-center justify-center"
                  onClick={onDismiss}
                >
                  <IconClose width={16} />
                </ButtonBase>
              </div>
            );
          }}
        >
          <div className="relative my-lg px-2sm md:my-0">
            <AgreementRenderer
              agreement={currentStepFormData?.documentInstance?.html}
              setTip={setAgreementTip}
              openTipModal={handleAgreementTipModalOpen}
              className="h-full"
              isIntersectionDisabled
            />
          </div>
        </Modal>
        {isDownloadingAgreementPDF && (
          <div className="fixed left-0 top-0 z-toast flex h-screen w-screen items-center justify-center bg-navy bg-white-translucent">
            <IconSpinner width={40} />
          </div>
        )}
        <Modal
          name="agreement-tip-modal"
          isOpen={isAgreementTipOverlayVisible}
          size="fullScreen"
          onDismiss={handleAgreementTipModalDismiss}
          contentClassName="md:h-min"
          closeButton={({ onDismiss }) => {
            return (
              <div
                className={clsx(
                  'absolute right-xs top-xs z-20 p-xs md:right-lg md:top-sm',
                )}
              >
                <Button onClick={onDismiss} variant="text">
                  {AGREEMENT_FLOW_STRINGS['sign.continueReading']}
                </Button>
              </div>
            );
          }}
        >
          <div className="mt-2xl px-2sm sm:mt-0">
            <SidebarWithTextBubble
              title=""
              description={agreementTip?.descriptions}
              employeeAssignment={employeeAssignment}
              makeGreen={false}
              showContact
              animateTextChange={false}
            />
          </div>
        </Modal>
      </div>
      <Toast
        isVisible={isErrorToastVisible}
        variant="danger"
        onClose={handleErrorToastClose}
      >
        {template(AGREEMENT_FLOW_STRINGS['sign.error'], {
          email: employeeAssignment?.employee?.workEmail ?? EMAIL,
          phoneNumber: formatPhoneNumber(
            employeeAssignment?.employee?.workPhone ?? DEFAULT_PHONE,
          ),
        })}
      </Toast>
    </>
  );
}
